import { useEffect, useRef } from 'react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * Fetches the settings used during the user's LAST recorded session for a
 * given pattern_id and calls `onSettingsLoaded(settings)` once when they are
 * received. Silently ignores errors or "no previous session" cases so the
 * modal keeps its default settings.
 *
 * @param {boolean} isOpen  Modal open flag. Fetch runs only when it becomes true.
 * @param {string}  patternId  Stable pattern id (e.g. 'box-breathing').
 * @param {(settings: object) => void} onSettingsLoaded Callback invoked with
 *   the saved settings dict (never null / empty). Not called when no previous
 *   session exists.
 */
export function useLastSessionSettings(isOpen, patternId, onSettingsLoaded) {
  const handledForOpenRef = useRef(false);
  const callbackRef = useRef(onSettingsLoaded);
  callbackRef.current = onSettingsLoaded;

  useEffect(() => {
    if (!isOpen) {
      handledForOpenRef.current = false;
      return;
    }
    if (handledForOpenRef.current) return;
    if (!patternId) return;
    handledForOpenRef.current = true;

    let cancelled = false;
    (async () => {
      try {
        const token = localStorage.getItem('token');
        if (!token) return;
        const { data } = await axios.get(`${API}/sessions/last`, {
          params: { pattern_id: patternId },
          headers: { Authorization: `Bearer ${token}` },
        });
        if (cancelled) return;
        if (data?.found && data.settings && typeof data.settings === 'object') {
          callbackRef.current?.(data.settings);
        }
      } catch (_err) {
        // Silent fail — modal keeps its defaults.
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [isOpen, patternId]);
}
